import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const LeagueSchedule = ({ eventId, leagueEvent }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [games, setGames] = useState([]);
  const [filter, setFilter] = useState("scheduled"); // "all" | "scheduled" | "completed"

  // Schedule dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [team1Id, setTeam1Id] = useState("");
  const [team2Id, setTeam2Id] = useState("");
  const [scheduledAt, setScheduledAt] = useState(dayjs());
  const [field, setField] = useState("");

  // Score dialog
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [scoringGame, setScoringGame] = useState(null);
  const [team1Score, setTeam1Score] = useState(0);
  const [team2Score, setTeam2Score] = useState(0);
  const [markPlayed, setMarkPlayed] = useState(true);

  // Delete confirm dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingGame, setDeletingGame] = useState(null);

  const teams = leagueEvent?.teams || [];
  const fields = leagueEvent?.fields || [];

  useEffect(() => {
    if (eventId) loadGames();
  }, [eventId]);

  const loadGames = async () => {
    const result = await request(`leaguegame?eventId=${eventId}`);
    if (result?.success) setGames(result.data);
  };

  const filteredGames = games.filter((g) => {
    if (filter === "scheduled") return !g.played;
    if (filter === "completed") return g.played;
    return true;
  });

  // Sort: scheduled games by upcoming first, completed by most recent first
  const sortedGames = [...filteredGames].sort((a, b) => {
    if (filter === "completed") {
      return dayjs(b.scheduledAt).valueOf() - dayjs(a.scheduledAt).valueOf();
    }
    return dayjs(a.scheduledAt).valueOf() - dayjs(b.scheduledAt).valueOf();
  });

  // ── Schedule dialog ──────────────────────────────────────────────

  const openCreate = () => {
    setEditingGame(null);
    setTeam1Id("");
    setTeam2Id("");
    setScheduledAt(dayjs());
    setField(fields[0] || "");
    setDialogOpen(true);
  };

  const openEdit = (game) => {
    setEditingGame(game);
    setTeam1Id(game.team1?._id);
    setTeam2Id(game.team2?._id);
    setScheduledAt(dayjs(game.scheduledAt));
    setField(game.field || "");
    setDialogOpen(true);
  };

  const handleSaveGame = async () => {
    let result;
    if (editingGame) {
      result = await request("leaguegame/update", "post", {
        gameId: editingGame._id,
        team1Id,
        team2Id,
        scheduledAt: scheduledAt?.toISOString(),
        field: field || undefined,
      });
    } else {
      result = await request("leaguegame/create", "post", {
        eventId,
        team1Id,
        team2Id,
        scheduledAt: scheduledAt?.toISOString(),
        field: field || undefined,
      });
    }
    if (result?.success) {
      toast.success(editingGame ? "Game updated" : "Game scheduled");
      setDialogOpen(false);
      await loadGames();
    }
  };

  // ── Score dialog ─────────────────────────────────────────────────

  const openScore = (game) => {
    setScoringGame(game);
    setTeam1Score(game.team1Score ?? 0);
    setTeam2Score(game.team2Score ?? 0);
    setMarkPlayed(game.played !== false); // default ON; stays ON for already-played games
    setScoreDialogOpen(true);
  };

  const handleSaveScore = async () => {
    const result = await request("leaguegame/score", "post", {
      gameId: scoringGame._id,
      team1Score: Number(team1Score),
      team2Score: Number(team2Score),
      played: markPlayed,
    });
    if (result?.success) {
      toast.success(markPlayed ? "Score recorded" : "Score saved (not marked as played)");
      setScoreDialogOpen(false);
      await loadGames();
    }
  };

  // ── Delete dialog ────────────────────────────────────────────────

  const openDelete = (game) => {
    setDeletingGame(game);
    setDeleteDialogOpen(true);
  };

  const handleDeleteGame = async () => {
    const result = await request("leaguegame/delete", "post", { gameId: deletingGame._id });
    if (result?.success) {
      toast.success("Game deleted");
      setDeleteDialogOpen(false);
      setDeletingGame(null);
      await loadGames();
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────

  const formatDate = (dt) => dayjs(dt).format("MMM D, YYYY");
  const formatTime = (dt) => dayjs(dt).format("h:mm A");

  const scoreLabel = (game) => {
    if (!game.played) return null;
    return `${game.team1Score} – ${game.team2Score}`;
  };

  const winnerOf = (game, side) => {
    if (!game.played) return false;
    if (game.team1Score === game.team2Score) return false;
    if (side === 1) return game.team1Score > game.team2Score;
    return game.team2Score > game.team1Score;
  };

  // ── Game card ────────────────────────────────────────────────────

  const GameCard = ({ game }) => (
    <Card variant="outlined">
      <CardContent sx={{ pb: "12px !important" }}>
        <Stack spacing={1}>
          {/* Date / time / field */}
          <Typography variant="caption" color="text.secondary">
            {formatDate(game.scheduledAt)} · {formatTime(game.scheduledAt)}
            {game.field ? ` · ${game.field}` : ""}
          </Typography>

          {/* Teams + score */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: winnerOf(game, 1) ? 700 : 400 }}
              >
                {game.team1?.name || "—"}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: winnerOf(game, 2) ? 700 : 400 }}
              >
                {game.team2?.name || "—"}
              </Typography>
            </Box>

            {game.played ? (
              <Box sx={{ textAlign: "right", minWidth: 48 }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, lineHeight: 1.1 }}
                >
                  {game.team1Score}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, lineHeight: 1.1 }}
                >
                  {game.team2Score}
                </Typography>
              </Box>
            ) : (
              <Chip label="Scheduled" size="small" variant="outlined" />
            )}
          </Box>

          {/* Status + actions */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
            <Chip
              label={game.played ? "Final" : "Upcoming"}
              size="small"
              color={game.played ? "success" : "default"}
              variant={game.played ? "filled" : "outlined"}
            />
            <Box>
              <IconButton size="small" onClick={() => openScore(game)} title="Enter / Edit Score">
                <SportsScoreIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => openEdit(game)} title="Edit Game">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => openDelete(game)} title="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  // ── Render ───────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: 1.5,
          mb: 2,
        }}
      >
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => { if (v) setFilter(v); }}
          size="small"
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton value="scheduled">Scheduled</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          fullWidth={isMobile}
        >
          Add Game
        </Button>
      </Box>

      {/* Game count */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        {sortedGames.length} game{sortedGames.length !== 1 ? "s" : ""}
      </Typography>

      {/* Cards */}
      {sortedGames.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: "center" }}>
          {filter === "scheduled"
            ? "No upcoming games."
            : filter === "completed"
            ? "No completed games yet."
            : "No games scheduled yet."}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sortedGames.map((game) => (
            <Grid item xs={12} sm={6} key={game._id}>
              <GameCard game={game} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Schedule / Edit Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{editingGame ? "Edit Game" : "Schedule Game"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Date & Time *"
                  value={scheduledAt}
                  onChange={(v) => setScheduledAt(v)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Team 1 *</InputLabel>
                <Select
                  value={team1Id}
                  label="Team 1 *"
                  onChange={(e) => setTeam1Id(e.target.value)}
                >
                  {teams.map((t) => (
                    <MenuItem
                      key={t.team?._id}
                      value={t.team?._id}
                      disabled={t.team?._id === team2Id}
                    >
                      {t.team?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Team 2 *</InputLabel>
                <Select
                  value={team2Id}
                  label="Team 2 *"
                  onChange={(e) => setTeam2Id(e.target.value)}
                >
                  {teams.map((t) => (
                    <MenuItem
                      key={t.team?._id}
                      value={t.team?._id}
                      disabled={t.team?._id === team1Id}
                    >
                      {t.team?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {fields.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Field</InputLabel>
                  <Select
                    value={field}
                    label="Field"
                    onChange={(e) => setField(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {fields.map((f) => (
                      <MenuItem key={f} value={f}>{f}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveGame}
            disabled={!team1Id || !team2Id || !scheduledAt}
          >
            {editingGame ? "Save" : "Schedule"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Score Dialog ── */}
      <Dialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {scoringGame?.played ? "Edit Score" : "Record Score"}
        </DialogTitle>
        <DialogContent>
          {scoringGame && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Team 1 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {scoringGame.team1?.name || "Team 1"}
                </Typography>
                <TextField
                  type="number"
                  inputProps={{ min: 0, style: { fontSize: 32, textAlign: "center" } }}
                  value={team1Score}
                  onChange={(e) => setTeam1Score(e.target.value)}
                  fullWidth
                />
              </Box>

              {/* Team 2 */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  {scoringGame.team2?.name || "Team 2"}
                </Typography>
                <TextField
                  type="number"
                  inputProps={{ min: 0, style: { fontSize: 32, textAlign: "center" } }}
                  value={team2Score}
                  onChange={(e) => setTeam2Score(e.target.value)}
                  fullWidth
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={markPlayed}
                    onChange={(e) => setMarkPlayed(e.target.checked)}
                    color="success"
                  />
                }
                label="Mark as Played"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveScore}
            disabled={Number(team1Score) < 0 || Number(team2Score) < 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this game?</DialogTitle>
        <DialogContent>
          {deletingGame && (
            <Typography>
              <strong>{deletingGame.team1?.name}</strong> vs <strong>{deletingGame.team2?.name}</strong>
              {" · "}{formatDate(deletingGame.scheduledAt)} {formatTime(deletingGame.scheduledAt)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteGame}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeagueSchedule;
